import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Plus, ExternalLink, Trash2, FileText, Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import PdfPreviewDialog from "@/components/PdfPreviewDialog";

export default function PresupuestosPage() {
  const [items, setItems] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [vehicleId, setVehicleId] = useState("");
  const [vehicleQuery, setVehicleQuery] = useState("");
  const [pdfPath, setPdfPath] = useState(null);
  const [pdfName, setPdfName] = useState("");
  const [search, setSearch] = useState("");
  const nav = useNavigate();

  const load = async () => {
    try {
      const [q, v, c] = await Promise.all([api.get("/quotes"), api.get("/vehicles"), api.get("/clients")]);
      setItems(q.data); setVehicles(v.data); setClients(c.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!vehicleId) { toast.error("Selecciona un expediente"); return; }
    try {
      const veh = vehicles.find(v => v.id === vehicleId);
      const r = await api.post("/quotes", {
        vehicle_id: vehicleId,
        client_id: veh?.client_id || null,
        lines: [], status: "draft", severity_split: false,
        disassembly_amount: 0, vat_rate: 21, notes: "",
      });
      setOpen(false); setVehicleId(""); nav(`/presupuestos/${r.data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!confirm("¿Borrar presupuesto?")) return;
    try { await api.delete(`/quotes/${id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const vehLabel = (id) => {
    const v = vehicles.find(x => x.id === id); if (!v) return "—";
    return `${v.plate} · ${v.brand || ""} ${v.model || ""}`.trim();
  };
  const clientName = (id) => clients.find(c => c.id === id)?.name || "—";

  // Combobox: filter vehicles by plate / brand / model / color / client name.
  const normalize = (s) =>
    (s || "")
      .toString()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[\s\-]/g, "");
  const filteredVehicles = useMemo(() => {
    const q = normalize(vehicleQuery);
    if (!q) return vehicles;
    return vehicles.filter((v) => {
      const haystack = [
        v.plate, v.brand, v.model, v.color, v.vin,
        clientName(v.client_id),
      ].map(normalize).join("|");
      return haystack.includes(q);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleQuery, vehicles, clients]);

  const selectedVehicle = vehicles.find(v => v.id === vehicleId);

  // Reset combobox when the dialog closes/opens.
  useEffect(() => {
    if (!open) { setVehicleId(""); setVehicleQuery(""); }
  }, [open]);

  const norm = (s) => (s || "").toString().toLowerCase();
  const filtered = items.filter((it) => {
    if (!search.trim()) return true;
    const n = norm(search);
    return [it.number, it.status, vehLabel(it.vehicle_id), clientName(it.client_id), (it.date || "").slice(0,10)]
      .some((f) => norm(f).includes(n));
  });

  return (
    <div data-testid="presupuestos-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="section-title">Presupuestos</h1>
          <p className="text-sm text-zinc-500 mt-1">Crea y gestiona presupuestos por expediente.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="btn-new-quote"><Plus size={16} className="mr-1" />Nuevo presupuesto</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nuevo presupuesto</DialogTitle></DialogHeader>
            <div>
              <Label className="text-xs">Expediente *</Label>
              <div className="relative mt-1">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input
                  autoFocus
                  data-testid="quote-vehicle-search"
                  placeholder="Busca por matrícula, marca, modelo, cliente…"
                  className="pl-8 pr-8"
                  value={vehicleQuery}
                  onChange={(e) => { setVehicleQuery(e.target.value); setVehicleId(""); }}
                />
                {(vehicleQuery || vehicleId) && (
                  <button
                    type="button"
                    onClick={() => { setVehicleQuery(""); setVehicleId(""); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
                    aria-label="Limpiar búsqueda"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>

              {selectedVehicle ? (
                <div
                  className="mt-2 border border-zinc-300 rounded-md p-2 text-sm flex items-center justify-between bg-zinc-50"
                  data-testid="quote-vehicle-selected"
                >
                  <span>
                    <span className="font-mono font-medium">{selectedVehicle.plate}</span>
                    <span className="text-zinc-500"> · {selectedVehicle.brand} {selectedVehicle.model}</span>
                    {selectedVehicle.client_id && (
                      <span className="text-zinc-400 text-xs ml-2">({clientName(selectedVehicle.client_id)})</span>
                    )}
                  </span>
                  <button
                    type="button"
                    className="text-xs text-zinc-500 hover:text-zinc-900"
                    onClick={() => { setVehicleId(""); setVehicleQuery(""); }}
                  >
                    Cambiar
                  </button>
                </div>
              ) : (
                <div
                  className="mt-2 border border-[var(--rule)] rounded-md max-h-64 overflow-y-auto"
                  data-testid="quote-vehicle-list"
                  role="listbox"
                >
                  {filteredVehicles.length === 0 ? (
                    <div className="text-xs text-zinc-400 px-3 py-4 text-center">
                      {vehicles.length === 0 ? "Crea primero un expediente." : "Sin resultados."}
                    </div>
                  ) : (
                    filteredVehicles.map((v) => (
                      <button
                        key={v.id}
                        type="button"
                        role="option"
                        onClick={() => { setVehicleId(v.id); setVehicleQuery(""); }}
                        className="w-full text-left px-3 py-2 hover:bg-zinc-100 border-b border-[var(--rule)] last:border-b-0 text-sm flex items-center justify-between"
                        data-testid={`quote-vehicle-option-${v.id}`}
                      >
                        <span>
                          <span className="font-mono font-medium">{v.plate}</span>
                          <span className="text-zinc-500"> · {v.brand || "—"} {v.model || ""}</span>
                        </span>
                        <span className="text-xs text-zinc-400">{clientName(v.client_id)}</span>
                      </button>
                    ))
                  )}
                </div>
              )}

              <p className="text-[11px] text-zinc-400 mt-1">
                {filteredVehicles.length} de {vehicles.length} expedientes
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button data-testid="btn-create-quote" onClick={create}>Crear y editar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mb-4 flex items-center gap-2 max-w-md">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            data-testid="quotes-search"
            placeholder="Buscar por número, matrícula, cliente, estado…"
            className="pl-8"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <span className="text-xs text-zinc-400">{filtered.length} / {items.length}</span>
      </div>

      <div className="page-card overflow-x-auto">
        <table className="tbl" data-testid="quotes-table">
          <thead><tr><th>Nº</th><th>Fecha</th><th>Expediente</th><th>Cliente</th><th>Estado</th><th></th></tr></thead>
          <tbody>
            {filtered.map((q) => (
              <tr key={q.id} data-testid={`quote-row-${q.id}`}>
                <td className="font-mono">{q.number}</td>
                <td>{(q.date || "").slice(0, 10)}</td>
                <td>{vehLabel(q.vehicle_id)}</td>
                <td>{clientName(q.client_id)}</td>
                <td><span className={`text-xs px-2 py-0.5 rounded-full ${q.status === "invoiced" ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-700"}`}>{q.status}</span></td>
                <td className="text-right whitespace-nowrap">
                  <Link to={`/presupuestos/${q.id}`} className="inline-block px-2 py-1 hover:bg-zinc-100 rounded" data-testid={`open-quote-${q.id}`}><ExternalLink size={14} /></Link>
                  <button onClick={() => { setPdfName(`presupuesto_${q.number}.pdf`); setPdfPath(`/quotes/${q.id}/pdf`); }} className="inline-block px-2 py-1 hover:bg-zinc-100 rounded" data-testid={`pdf-quote-${q.id}`}><FileText size={14} /></button>
                  <button onClick={() => del(q.id)} className="px-2 py-1 hover:bg-red-50 text-red-600 rounded"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan="6" className="text-center text-zinc-400 py-10">{items.length === 0 ? "Sin presupuestos todavía." : "Sin resultados."}</td></tr>}
          </tbody>
        </table>
      </div>

      <PdfPreviewDialog path={pdfPath} filename={pdfName} onClose={() => setPdfPath(null)} />
    </div>
  );
}
